import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Trophy, Target, TrendingUp } from 'lucide-react';
import { RatingBadge } from './RatingBadge';
import { PlayerStats } from '@/types/stats';
import { RatingHistoryChart } from '@/components/stats/RatingHistoryChart';

interface PlayerDrawerProps {
  player: PlayerStats | null;
  open: boolean;
  onClose: () => void;
}

export function PlayerDrawer({ player, open, onClose }: PlayerDrawerProps) {
  if (!player) return null;

  const winRate = player.games_played > 0 ? player.win_rate * 100 : 0;
  const lossRate = player.games_played > 0 ? (player.losses / player.games_played) * 100 : 0;

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {player.name}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Player Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Player Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Current Level:</span>
                <RatingBadge rating={player.rating} deviation={player.rating_deviation} />
              </div>
              
              {player.phone && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Phone:</span>
                  <span className="text-sm">{player.phone}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Member Since:</span>
                <span className="text-sm">{new Date(player.created_at).toLocaleDateString()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5" />
                Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{player.wins}</div>
                  <div className="text-sm text-muted-foreground">Wins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{player.losses}</div>
                  <div className="text-sm text-muted-foreground">Losses</div>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Win Rate:</span>
                  <span className="text-sm font-bold">{winRate.toFixed(1)}%</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Games Played:</span>
                  <span className="text-sm">{player.games_played}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rating Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Rating Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Current Rating:</span>
                  <span className="text-sm font-mono">{Math.round(player.rating)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Confidence Interval:</span>
                  <span className="text-sm font-mono">
                    {Math.round(player.confidence_interval[0])} - {Math.round(player.confidence_interval[1])}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Uncertainty:</span>
                  <span className="text-sm">±{Math.round(1.96 * player.rating_deviation)}</span>
                </div>
              </div>

              <div className="pt-4 border-t">
                <div className="text-sm text-muted-foreground mb-2">Estimated Level Range:</div>
                <div className="text-xs space-y-1">
                  <div>• Beginner: 0-1400</div>
                  <div>• Intermediate: 1400-1700</div>
                  <div>• Advanced: 1700-2000</div>
                  <div>• Expert: 2000+</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Form</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge variant={
                  player.recent_form === 'up' ? 'default' :
                  player.recent_form === 'down' ? 'destructive' : 'secondary'
                }>
                  {player.recent_form === 'up' ? 'Improving' :
                   player.recent_form === 'down' ? 'Declining' : 'Stable'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Based on rating volatility and recent performance
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Rating History Chart */}
          <RatingHistoryChart playerId={player.id} height={300} />
        </div>
      </SheetContent>
    </Sheet>
  );
}